import { format } from 'date-fns';
import { Link } from 'react-router-dom';

import { Avatar, TableCell, TableRow, Typography } from '@/ui';
import { capitalize } from '@/utils';

import type { ViewerSessionZone } from '@/features/membersTable/viewerSessionTime';
import type { CompanyMember } from '@/store/api/companyApi';
import type { SessionCount, User } from '@/types';
import type { FC } from 'react';

import CoachChip from '@/components/Company/Members/List/CoachChip';
import SessionColumn from '@/components/Company/Members/List/SessionColumn';
import {
  AdminNoteCell,
  getSeatTypeLabel,
  renderGrowthPlanIcon,
  renderStatusCellContent,
} from '@/features/companyDashboard/common/members/MemberRowShared';
import { MemberCohortTagsCell } from '@/features/membersTable/CohortTagsCell';
import { formatContractEndValue } from '@/utils/contractEnd';

const renderCoachCellContent = (user: User) => {
  if (user.coaches.length) {
    return (
      <div className="flex flex-col items-start gap-1">
        {user.coaches.map((coach) => (
          <CoachChip
            key={`coach-avatar-${coach.id}-${user.id}`}
            coach={coach}
          />
        ))}
      </div>
    );
  }

  return (
    <Typography color="text.secondary" variant="caption">
      No coach assigned
    </Typography>
  );
};

const renderNameCell = (user: User, profileUrl: string) => (
  <Link className="flex items-center gap-2 no-underline" to={profileUrl}>
    <Avatar
      firstName={user.firstName}
      lastName={user.lastName}
      src={user.avatarUrl ?? ''}
    />
    <div>
      <Typography color="text.primary" variant="subtitle1">
        {user.firstName} {user.lastName}
      </Typography>
      <Typography color="text.secondary" variant="caption">
        {user.profile?.title}
      </Typography>
    </div>
  </Link>
);

const renderContractEndCellContent = (user: User) => (
  <Typography color="text.secondary" variant="caption">
    {formatContractEndValue(user.contractEnd) ?? '—'}
  </Typography>
);

interface OpsMembersRowProps {
  companyId: string;
  sessionCount: SessionCount;
  viewerZone: ViewerSessionZone;
  user: CompanyMember;
}

const OpsMembersRow: FC<OpsMembersRowProps> = ({
  companyId,
  sessionCount,
  user,
  viewerZone,
}) => {
  const hasStartedGrowthPlan = user.growthPlans[0]?.started || false;

  const insightStatus = capitalize(
    user.insightsStatus?.replace('_', ' ') || '',
  );

  const profileUrl = `/coach/members/${user.id}`;
  const upcomingSessionsUrl = `/coach/members/${user.id}/sessions/upcoming`;
  const pastSessionsUrl = `/coach/members/${user.id}/sessions/past`;

  return (
    <TableRow>
      <TableCell>{renderNameCell(user, profileUrl)}</TableCell>
      <TableCell>{renderStatusCellContent(user)}</TableCell>
      <TableCell>
        <SessionColumn
          pastSessionsUrl={pastSessionsUrl}
          upcomingSessionsUrl={upcomingSessionsUrl}
          viewerZone={viewerZone}
          linkToMemberPages
          {...sessionCount}
        />
      </TableCell>
      <TableCell>{renderCoachCellContent(user)}</TableCell>
      <TableCell>{renderContractEndCellContent(user)}</TableCell>
      <TableCell className="!whitespace-nowrap">
        <MemberCohortTagsCell
          companyId={companyId}
          tags={user.cohortTags}
          userId={user.id}
        />
      </TableCell>
      <TableCell>
        <Typography color="text.secondary" variant="caption">
          {getSeatTypeLabel(user)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography color="text.secondary" variant="caption">
          {format(Date.parse(user.createdAt), 'MMM d yyyy')}
        </Typography>
      </TableCell>
      <TableCell className="w-24 text-center md:table-cell">
        {renderGrowthPlanIcon(hasStartedGrowthPlan)}
      </TableCell>
      <TableCell className="md:table-cell">{insightStatus}</TableCell>
      <TableCell>
        <AdminNoteCell initialAdminNote={user.adminNote} userId={user.id} />
      </TableCell>
    </TableRow>
  );
};

export default OpsMembersRow;
